import type { FC } from "react"
import { useMemo } from "react"
import { ChevronDownIcon, ChevronLeftIcon } from "@chakra-ui/icons"
import { Box, Flex, Stack, Text, useBoolean } from "@chakra-ui/react"
import { BigNumber } from "@ethersproject/bignumber"
import invariant from "tiny-invariant"

import { getWeightLabel } from "common/helpers/bignumber"

type VotingModalVotingPowerProps = {
  votingPower: string | BigNumber
  decimals?: number
  isGovernorVote?: boolean
}

const VotingModalVotingPower: FC<VotingModalVotingPowerProps> = ({
  votingPower,
  decimals,
  isGovernorVote = false,
}) => {
  const weightLabel = useMemo(() => {
    const bigVotingPower = votingPower ? BigNumber.from(votingPower) : "0"

    invariant(
      decimals !== undefined,
      "`decimals` must be defined for governor votes",
    )

    return getWeightLabel(bigVotingPower, decimals)
  }, [votingPower, decimals])

  const [shouldDisplayExplanation, setShouldDisplayExplanation] =
    useBoolean(false)
  const explanation = useMemo(() => {
    if (!isGovernorVote) {
      return null
    }

    return (
      <Stack>
        <Text fontSize="xs">
          Your voting power is calculated as the number of tokens (votes) that
          have been delegated to you before the proposal became active. You can
          delegate your votes to yourself, or to someone else. Others can also
          delegate their votes to you.
        </Text>
        <Text fontSize="xs">
          For votes that come from tokens that you own, if you delegated to
          yourself after this proposal became active, they will not be counted
          towards your voting power for this proposal. These votes will be
          counted towards your voting power for future proposals.
        </Text>
        <Text fontSize="xs">
          This behavior is intended to prevent users from changing the outcome
          of a vote in progress by buying or borrowing additional votes.
        </Text>
      </Stack>
    )
  }, [isGovernorVote])

  return (
    <Box border="1.5px solid" borderColor="gray.100" m={6} rounded="lg">
      <Box p={4}>
        <Text color="gray.500" fontWeight={400}>
          Voting power
        </Text>
        <Text fontSize="3xl">{weightLabel}</Text>
      </Box>
      <Stack borderTop="1.5px solid" borderTopColor="gray.100" px={4} py={2}>
        <Flex
          align="center"
          cursor="pointer"
          justify="space-between"
          onClick={() => setShouldDisplayExplanation.toggle()}
        >
          <Text color="gray.500" fontSize="xs" fontWeight="semibold">
            How is my voting power calculated?
          </Text>
          {shouldDisplayExplanation ? <ChevronDownIcon /> : <ChevronLeftIcon />}
        </Flex>
        {shouldDisplayExplanation && explanation}
      </Stack>
    </Box>
  )
}

export default VotingModalVotingPower
